import { useCallback, useEffect, useState } from 'react';
import type { WidgetProps } from '@frontend/widgets/types';
import { fetchWidgetData } from '../api';

interface ReviewPullRequest {
	id: number;
	number: number;
	title: string;
	author: string;
	createdAt: string | null;
	updatedAt: string | null;
	htmlUrl: string | null;
	repo: string;
	projectId: string;
	projectName: string;
}

interface Warning {
	projectId: string;
	repo: string;
	message: string;
}

function relativeAge(iso: string | null): string {
	if (!iso) return '';
	const ms = Date.now() - new Date(iso).getTime();
	const hours = Math.floor(ms / 3_600_000);
	if (hours < 1) return 'just now';
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

export default function GiteaReviewNotificationsWidget({
	contextType,
	contextId,
	ecosystemId,
}: WidgetProps) {
	const [pullRequests, setPullRequests] = useState<ReviewPullRequest[]>([]);
	const [warnings, setWarnings] = useState<Warning[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const load = useCallback(() => {
		setLoading(true);
		setError('');
		fetchWidgetData<{ pullRequests: ReviewPullRequest[]; warnings?: Warning[] }>(
			'gitea-review-notifications',
			{ ecosystemId, contextType, contextId }
		)
			.then((d) => {
				setPullRequests(d.pullRequests || []);
				setWarnings(d.warnings || []);
			})
			.catch((err: Error) => setError(err.message))
			.finally(() => setLoading(false));
	}, [contextType, contextId, ecosystemId]);

	useEffect(() => {
		load();
	}, [load]);

	if (loading) return <div className="widget-inner-loading">Loading review requests…</div>;
	if (error) {
		return (
			<div className="widget-inner-error">
				{error}
				<button type="button" className="btn widget-refresh-btn" onClick={load}>
					Retry
				</button>
			</div>
		);
	}

	return (
		<div className="widget-review-notifications">
			<div className="widget-toolbar">
				<button type="button" className="btn widget-refresh-btn" onClick={load}>
					Refresh
				</button>
			</div>
			{warnings.length > 0 ? (
				<div className="widget-inner-warning">
					{warnings.length} repository warning{warnings.length === 1 ? '' : 's'} while loading.
				</div>
			) : null}
			{!pullRequests.length ? (
				<div className="widget-inner-empty">No open pull requests need your review.</div>
			) : (
				<ul className="widget-list">
					{pullRequests.map((pr) => (
						<li key={`${pr.repo}-${pr.number}`} className="widget-list-item">
							<div className="widget-list-primary">
								{pr.htmlUrl ? (
									<a href={pr.htmlUrl} target="_blank" rel="noopener noreferrer">
										#{pr.number} {pr.title}
									</a>
								) : (
									<span>
										#{pr.number} {pr.title}
									</span>
								)}
							</div>
							<div className="widget-list-meta">
								<span>{pr.projectName || pr.repo}</span>
								<span>{pr.author}</span>
								<span>{relativeAge(pr.updatedAt || pr.createdAt)}</span>
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
